import { FieldRenderProps } from '@progress/kendo-react-form';
import React, { useContext, useState } from 'react';
import { Upload, UploadListItemProps, UploadOnAddEvent, UploadOnRemoveEvent } from '@progress/kendo-react-upload';
import { FormFieldWrapper } from '../FormFieldWrapper';
import { SvgIcon } from '@progress/kendo-react-common';
import { downloadIcon, trashIcon } from '@progress/kendo-svg-icons';
import {
  DocumentsContext,
  useGetFileExtensions,
  useGlobalizationContext,
  useTranslation
} from '@cs-cms/state-management';
import { EditedDocument, MAX_FILE_SIZE } from '@cs-cms/types';
import { ExtendedUploadFileInfo } from '../../types/ExtendedUploadFileInfo';
import {
  buildAddedUploadValue,
  findFileWithDisallowedExtension,
  getMaxFilesLimitErrorMessage,
  triggerFileSelectIfNeeded
} from './formUploadHelpers';

interface FormUploadProps extends FieldRenderProps {
  editedDocument: 'new' | EditedDocument;
  ctName?: string;
  multiple?: boolean;
  filesRenderedElsewhere?: boolean;
  maxFiles?: number;
  onFileSelect?: (files: FileList) => void;
  isProcessing?: boolean;
}

export const FormUpload: React.FC<FormUploadProps> = fieldRenderProps => {
  const { disabled, editedDocument, value, multiple, filesRenderedElsewhere, maxFiles, onFileSelect, isProcessing } =
    fieldRenderProps;
  const documentsContext = useContext(DocumentsContext);
  const { data: allowedExtensions } = useGetFileExtensions();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const [validationError, setValidationError] = useState<string | null>(null);

  const getCurrentFiles = () => {
    if (!value) {
      return [];
    }
    if (!Array.isArray(value)) {
      return [value];
    }

    if (multiple) {
      return value.map(item => item.file || item);
    }

    return value;
  };

  const currFiles = getCurrentFiles();

  const onChangeHandler = (event: UploadOnAddEvent): void => {
    const newFiles = event.newState;

    if (findFileWithDisallowedExtension(newFiles, allowedExtensions)) {
      setValidationError(translate('custom.common.validation.fileTypeNotAllowed'));
      return;
    }

    const maxFilesError = getMaxFilesLimitErrorMessage(newFiles.length, currFiles.length, maxFiles, {
      fileLimitReached: translate('custom.common.validation.fileLimitReached'),
      fileLimitExceeded: max => translate('custom.common.validation.fileLimitExceeded', { maxFiles: max })
    });
    if (maxFilesError) {
      setValidationError(maxFilesError);
      return;
    }

    setValidationError(null);
    fieldRenderProps.onChange({ value: buildAddedUploadValue(newFiles, multiple, value) });
    triggerFileSelectIfNeeded(onFileSelect, newFiles);
  };

  const onRemoveHandler = (event: UploadOnRemoveEvent): void => {
    if (multiple) {
      const filesWithMetadata = event.newState.map(file => ({
        file,
        perFileMetadata: {}
      }));
      fieldRenderProps.onChange({ value: filesWithMetadata });
    } else {
      fieldRenderProps.onChange({ value: event.newState });
    }
    setValidationError(null);
  };

  const removeFile = (fileToRemove: ExtendedUploadFileInfo): void => {
    if (multiple) {
      const updatedFiles = currFiles.filter(item => {
        const file = item.file || item;
        return file !== fileToRemove;
      });
      fieldRenderProps.onChange({ value: updatedFiles });
    } else {
      const updatedFiles = currFiles.filter(file => file !== fileToRemove);
      fieldRenderProps.onChange({ value: updatedFiles });
    }
    setValidationError(null);
  };

  const handleDownload = async (documentId: string) => {
    if (editedDocument === 'new' || !documentsContext.openDocument) {
      return;
    }

    try {
      documentsContext.openDocument(editedDocument.Cps_ContentType, documentId);
    } catch {
      // empty
    }
  };

  const customItemRenderer = (props: UploadListItemProps): React.JSX.Element => {
    const extendedFiles = props.files as ExtendedUploadFileInfo[];

    return (
      <>
        {extendedFiles.map((file, index) => (
          <div key={file.uid || index} className="k-file">
            <div className="attachmentItem k-file-single">
              <div
                className="attachmentName"
                onClick={() => file.documentId && handleDownload(file.documentId)}
                style={{ cursor: file.documentId ? 'pointer' : 'default' }}
              >
                {file.name}
              </div>
              <div className="attachmentActionButtons">
                {file.documentId && (
                  <button
                    type="button"
                    className="k-button k-button-md k-button-flat k-button-flat-primary"
                    onClick={() => file.documentId && handleDownload(file.documentId)}
                    title="Download file"
                  >
                    <SvgIcon icon={downloadIcon} size="medium" />
                  </button>
                )}
                {!disabled && (
                  <button
                    type="button"
                    className="k-button k-button-md k-button-flat k-button-flat-primary"
                    onClick={() => removeFile(file)}
                    title="Delete file"
                  >
                    <SvgIcon icon={trashIcon} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <FormFieldWrapper
      validationMessage={fieldRenderProps.validationMessage}
      value={fieldRenderProps.value}
      touched={fieldRenderProps.touched}
      id={fieldRenderProps.id}
      valid={fieldRenderProps.valid}
      disabled={fieldRenderProps.disabled}
      hint={fieldRenderProps.hint}
      label={fieldRenderProps.label}
      wrapperStyle={fieldRenderProps.wrapperStyle}
      optional={fieldRenderProps.optional}
      modified={fieldRenderProps.modified}
      visited={fieldRenderProps.visited}
      onChange={fieldRenderProps.onChange}
      onFocus={fieldRenderProps.onFocus}
      onBlur={fieldRenderProps.onBlur}
      name={fieldRenderProps.name}
      isLoading={isProcessing}
    >
      <Upload
        autoUpload={false}
        showActionButtons={false}
        showFileList={!filesRenderedElsewhere}
        listItemUI={customItemRenderer}
        files={currFiles}
        onAdd={onChangeHandler}
        onRemove={onRemoveHandler}
        disabled={disabled || isProcessing}
        className="cpsFormUpload"
        multiple={multiple}
        restrictions={{
          maxFileSize: MAX_FILE_SIZE,
          allowedExtensions
        }}
      />
      {validationError && (
        <div className="k-form-error" role="alert">
          {validationError}
        </div>
      )}
    </FormFieldWrapper>
  );
};
